/**
 * Central config + content for Grade 11 Programming & AI curriculum SEO page.
 * Course IDs verified from live API (GET /courses): intro=8 (free), month1=9.
 */
export const grade11CurriculumConfig = {
    academicYear: '2026/2027',
    lastReviewedDate: '2026-08-20',
    canonicalUrl: 'https://codingwithghonaim.com/grade-11-programming-ai-curriculum',
    routePath: '/grade-11-programming-ai-curriculum',
    routeName: 'grade-11-programming-ai-curriculum',

    arabicTerm1BookUrl: '/downloads/grade-11-programming-ai-arabic-book-term-1-2026-2027.pdf',
    arabicTerm2BookUrl: '/downloads/grade-11-programming-ai-arabic-book-term-2-2026-2027.pdf',
    englishTerm1BookUrl: '/downloads/grade-11-programming-ai-english-book-term-1-2026-2027.pdf',
    englishTerm2BookUrl: '/downloads/grade-11-programming-ai-english-book-term-2-2026-2027.pdf',
    arabicTerm1BookSize: '31.3 MB',
    arabicTerm2BookSize: '19.4 MB',
    englishTerm1BookSize: '25.0 MB',
    englishTerm2BookSize: '17.3 MB',

    // Real course routes from API
    introCourseUrl: '/courses/8',
    introCourseRoute: { name: 'course', params: { id: 8 } },
    monthOneCourseUrl: '/courses/9',
    monthOneCourseRoute: { name: 'course', params: { id: 9 } },

    aboutGhonaimUrl: '/about',
    aboutGhonaimRoute: { name: 'about' },
    grade10CurriculumUrl: '/grade-10-programming-ai-curriculum',
    grade10CurriculumRoute: { name: 'grade-10-programming-ai-curriculum' },
    mainGrade11CourseUrl: '/grade-11',
    mainGrade11CourseRoute: { name: 'grade-11' },
    homeUrl: '/',
    homeRoute: { name: 'welcome' },

    // Purchase / activation happens on the Month 1 course page (wallet requires auth).
    paymentInfoUrl: '/courses/9',
    paymentInfoRoute: { name: 'course', params: { id: 9 } },

    // Branded 1200×630 Open Graph image for social crawlers
    socialImageUrl: 'https://codingwithghonaim.com/images/seo/grade-11-curriculum/grade-11-programming-ai-curriculum-og.jpg',
};

/**
 * Official Grade 11 curriculum map (Coding With Ghonaim).
 * Filename is an SEO keyword slug; dimensions match the exported PNG.
 */
export const curriculumMap = {
    id: 'grade-11-curriculum-map',
    label: 'خريطة منهج البرمجة والذكاء الاصطناعي تانية ثانوي بكالوريا 2026/2027',
    shortLabel: 'خريطة المنهج',
    title: 'خريطة منهج البرمجة والذكاء الاصطناعي تانية ثانوي بكالوريا 2026/2027 | Coding With Ghonaim',
    alt:
        'خريطة منهج البرمجة والذكاء الاصطناعي تانية ثانوي بكالوريا 2026/2027 — 7 فصول مترابطة: تكنولوجيا المعلومات والذكاء الاصطناعي، الأمن، تطبيقات الويب، تصميم المعلومات، جمع البيانات، تحليل البيانات، Machine Learning وGenerative AI — Coding With Ghonaim',
    caption:
        'خريطة منهج تانية ثانوي بكالوريا: سبعة فصول مترابطة من الأنظمة الرقمية والأمن والويب إلى البيانات والتعلم الآلي والذكاء الاصطناعي التوليدي.',
    type: 'curriculum-map',
    src: '/images/seo/grade-11-curriculum/grade-11-programming-ai-curriculum-map-2026-2027.png',
    width: 819,
    height: 1024,
    pending: false,
    keywords: [
        'خريطة منهج تانية ثانوي',
        'منهج البرمجة تانية ثانوي بكالوريا',
        'Programming AI Grade 11',
        'Machine Learning',
        'Coding With Ghonaim',
    ],
};

/**
 * Hero journey banner (design asset) — shown between breadcrumbs and hero copy.
 */
export const journeyBanner = {
    src: '/images/seo/grade-11-curriculum/grade-11-programming-ai-curriculum-journey-banner.png',
    alt:
        'بانر منهج البرمجة والذكاء الاصطناعي تانية ثانوي بكالوريا 2026/2027 — رحلة 7 فصول مترابطة: IT، Security، Web، Design، Data، Analysis، AI — البرمجة مع غنيم',
    width: 1536,
    height: 1024,
};

export const curriculumIntroVideo = {
    title: 'تعرّف على منهج تانية ثانوي',
    subtitle: 'شاهد الفيديو ده عشان تعرف شكل المنهج والفصول السبعة وإزاي تبدأ صح.',
    youtubeId: 'GHkdhbfqV6k',
    watchUrl: 'https://youtu.be/GHkdhbfqV6k',
};

export const curriculumChapters = [
    {
        id: 'chapter-1-information-technology-ai',
        number: 1,
        arabicTitle: 'تكنولوجيا المعلومات والذكاء الاصطناعي',
        englishTitle: 'Information Technology & AI',
        icon: 'Cpu',
        introduction: '',
        paragraphs: [
            'في بداية المنهج، هتفهم الصورة الكبيرة لعالم التكنولوجيا.',
            'هتتعرف على معنى تكنولوجيا المعلومات، ومكوّنات الأنظمة الرقمية، ودور الأجهزة والبرامج والبيانات والأشخاص في تشغيل أي نظام.',
            'وهتبدأ تكتشف مفهوم الذكاء الاصطناعي، والفرق بين البرامج التقليدية والأنظمة الذكية، وإزاي أصبح AI جزءًا من التطبيقات اللي بنستخدمها كل يوم.',
        ],
        concepts: [
            'Information Technology',
            'Digital Systems',
            'Hardware',
            'Software',
            'Data',
            'Users',
            'Artificial Intelligence',
            'Traditional Programs vs Intelligent Systems',
        ],
        coreIdea: 'فهم العالم الرقمي والأنظمة التي نستخدمها يوميًا.',
    },
    {
        id: 'chapter-2-security-risk',
        number: 2,
        arabicTitle: 'تقنيات الأمن وإدارة المخاطر',
        englishTitle: 'Security Technologies & Risk Management',
        icon: 'Shield',
        paragraphs: [
            'قبل ما نبني أي نظام رقمي، لازم نعرف إزاي نحميه.',
            'في الفصل ده، هتتعرف على طرق حماية الحسابات والأنظمة والبيانات من الاختراق والسرقة والاستخدام غير المسموح به.',
            'هتدرس أفكارًا مثل التشفير، كلمات المرور الآمنة، التحقق من الهوية، الصلاحيات، التهديدات الرقمية وإدارة المخاطر.',
            'وهتتعلم كمان إزاي تستخدم الإنترنت والمنصات الرقمية بطريقة أكثر أمانًا.',
        ],
        concepts: [
            'Encryption',
            'Password Security',
            'Authentication',
            'Authorization',
            'Permissions',
            'Digital Threats',
            'Risk Management',
            'Privacy',
        ],
        coreIdea: 'أي نظام جيد لازم يكون آمنًا قبل ما يكون متطورًا.',
    },
    {
        id: 'chapter-3-web-applications',
        number: 3,
        arabicTitle: 'تطبيقات الويب',
        englishTitle: 'Web Applications',
        icon: 'Globe',
        openingQuestion: 'إيه اللي بيحصل لما تسجل دخولك على منصة تعليمية؟ وإزاي الموقع يعرف حسابك ويفتح الكورس الخاص بيك؟',
        paragraphs: [
            'الفصل ده بيشرح طريقة عمل المواقع والتطبيقات اللي بنستخدمها يوميًا.',
            'هتتعرف على رحلة البيانات من المستخدم إلى الخادم، وعلى دور الواجهة الأمامية، والجزء الخلفي، وقواعد البيانات، وواجهات البرمجة.',
            'ومن خلال أمثلة مثل المنصات التعليمية والمتاجر الإلكترونية، هتفهم ما يحدث خلف الشاشة عند الضغط على زر أو إرسال بيانات.',
        ],
        concepts: [
            'Frontend',
            'Backend',
            'Server',
            'Database',
            'API',
            'Request',
            'Response',
            'User Authentication',
            'Data Flow',
        ],
        coreIdea: 'فهم كيف تتواصل أجزاء تطبيق الويب لتقديم خدمة كاملة للمستخدم.',
    },
    {
        id: 'chapter-4-websites-information-design',
        number: 4,
        arabicTitle: 'المواقع الإلكترونية وتصميم المعلومات',
        englishTitle: 'Websites & Information Design',
        icon: 'Layout',
        paragraphs: [
            'الموقع الناجح مش بس موقع بيشتغل. لازم يكون واضحًا، منظمًا، وسهل الاستخدام.',
            'في الفصل ده، هتتعرف على كيفية تنظيم المعلومات داخل الموقع، وتصميم الصفحات، وتوجيه المستخدم، واختيار العناصر المرئية المناسبة.',
            'وهتبدأ تفهم مفاهيم تجربة المستخدم وواجهة المستخدم وسهولة الوصول واختبار التصميم وتحسينه.',
        ],
        concepts: [
            'Information Architecture',
            'User Interface',
            'User Experience',
            'Navigation',
            'Accessibility',
            'Layout',
            'Usability',
            'User Testing',
        ],
        coreIdea: 'التصميم الجيد يساعد المستخدم يوصل لهدفه بسهولة.',
    },
    {
        id: 'chapter-5-data-collection',
        number: 5,
        arabicTitle: 'جمع البيانات وإعدادها',
        englishTitle: 'Data Collection & Preparation',
        icon: 'Database',
        paragraphs: [
            'أي نظام ذكي يحتاج إلى بيانات جيدة.',
            'في الفصل ده، هتتعرف على مصادر البيانات وطرق جمعها وتنظيمها.',
            'وهتتعلم إزاي تكتشف السجلات المكررة، والقيم الناقصة، والبيانات غير الصحيحة، واختلاف أشكال البيانات، والأخطاء التي تؤثر في النتائج.',
            'وهتفهم ليه لازم ننظف البيانات ونجهزها قبل استخدامها في التحليل أو الذكاء الاصطناعي.',
        ],
        concepts: [
            'Data Sources',
            'Data Collection',
            'Missing Values',
            'Duplicate Records',
            'Data Errors',
            'Data Cleaning',
            'Data Validation',
            'Data Preparation',
        ],
        coreIdea: 'جودة النتائج تبدأ من جودة البيانات.',
    },
    {
        id: 'chapter-6-data-analysis',
        number: 6,
        arabicTitle: 'تحليل البيانات وعرضها',
        englishTitle: 'Data Analysis & Communication',
        icon: 'BarChart3',
        paragraphs: [
            'بعد جمع البيانات وتجهيزها، نبدأ نبحث عن المعنى اللي موجود بداخلها.',
            'في الفصل ده، هتتعرف على طرق وصف البيانات، واكتشاف الاتجاهات والعلاقات، واستخدام الجداول والرسوم البيانية لعرض النتائج.',
            'وهتفهم إن اختيار الرسم البياني المناسب وطريقة عرض المعلومات ممكن يؤثروا بشكل كبير في فهم النتيجة.',
        ],
        showDataFlow: true,
        concepts: [
            'Data Description',
            'Trends',
            'Relationships',
            'Tables',
            'Charts',
            'Data Visualization',
            'Communication',
            'Decision Making',
        ],
        coreIdea: 'تحويل الأرقام إلى معلومات تساعدنا على الفهم واتخاذ القرار.',
    },
    {
        id: 'chapter-7-machine-learning-generative-ai',
        number: 7,
        arabicTitle: 'التعلم الآلي والذكاء الاصطناعي التوليدي',
        englishTitle: 'Machine Learning & Generative AI',
        icon: 'Brain',
        paragraphs: [
            'هنا بتوصل لواحد من أكثر أجزاء منهج البرمجة تانية ثانوي إثارة.',
            'هتتعرف على كيفية تعلم الآلة من البيانات والأمثلة، وإزاي يتم تدريب النماذج واختبارها واستخدامها في التوقع أو التصنيف.',
            'كما ستتعرف على الشبكات العصبية والذكاء الاصطناعي التوليدي، وإزاي الأنظمة الحديثة تقدر تنشئ النصوص والصور والصوت والفيديو والأكواد البرمجية.',
            'الفصل بيتناول كمان الاستخدام المسؤول للذكاء الاصطناعي، وضرورة مراجعة نتائجه وعدم الاعتماد عليه بدون تفكير.',
        ],
        concepts: [
            'Machine Learning',
            'Training Data',
            'Testing Data',
            'Models',
            'Prediction',
            'Classification',
            'Neural Networks',
            'Generative AI',
            'AI Responsibility',
            'Human Review',
        ],
        coreIdea: 'فهم كيف تتعلم الأنظمة الذكية وكيف نستخدمها بطريقة صحيحة ومسؤولة.',
    },
];

export const downloadBooks = [
    {
        id: 'ar-term-1',
        language: 'ar',
        term: 1,
        title: 'كتاب البرمجة الرسمي — عربي · الترم الأول',
        url: grade11CurriculumConfig.arabicTerm1BookUrl,
        fileName: 'grade-11-programming-ai-arabic-book-term-1-2026-2027.pdf',
        fileSize: grade11CurriculumConfig.arabicTerm1BookSize,
        details: [
            'الصف الثاني الثانوي بكالوريا',
            `العام الدراسي ${grade11CurriculumConfig.academicYear}`,
            'كتاب الوزارة الرسمي',
            'الترم الأول',
            'طلاب العربي',
            'PDF',
        ],
        sheetLabel: 'عربي — الترم الأول',
        downloadLabel: 'تحميل الترم الأول PDF',
        openLabel: 'فتح الكتاب',
        trackDownload: 'seo_grade11_arabic_term1_book_download',
        trackOpen: 'seo_grade11_arabic_term1_book_open',
    },
    {
        id: 'ar-term-2',
        language: 'ar',
        term: 2,
        title: 'كتاب البرمجة الرسمي — عربي · الترم الثاني',
        url: grade11CurriculumConfig.arabicTerm2BookUrl,
        fileName: 'grade-11-programming-ai-arabic-book-term-2-2026-2027.pdf',
        fileSize: grade11CurriculumConfig.arabicTerm2BookSize,
        details: [
            'الصف الثاني الثانوي بكالوريا',
            `العام الدراسي ${grade11CurriculumConfig.academicYear}`,
            'كتاب الوزارة الرسمي',
            'الترم الثاني',
            'طلاب العربي',
            'PDF',
        ],
        sheetLabel: 'عربي — الترم الثاني',
        downloadLabel: 'تحميل الترم الثاني PDF',
        openLabel: 'فتح الكتاب',
        trackDownload: 'seo_grade11_arabic_term2_book_download',
        trackOpen: 'seo_grade11_arabic_term2_book_open',
    },
    {
        id: 'en-term-1',
        language: 'en',
        term: 1,
        title: 'Official Programming Book — English · Term 1',
        url: grade11CurriculumConfig.englishTerm1BookUrl,
        fileName: 'grade-11-programming-ai-english-book-term-1-2026-2027.pdf',
        fileSize: grade11CurriculumConfig.englishTerm1BookSize,
        details: [
            'Second Year Secondary — Baccalaureate',
            `Academic Year ${grade11CurriculumConfig.academicYear}`,
            'Official ministry book',
            'Term 1',
            'Language schools',
            'PDF',
        ],
        sheetLabel: 'English — Term 1',
        downloadLabel: 'Download Term 1 PDF',
        openLabel: 'Open Book',
        trackDownload: 'seo_grade11_english_term1_book_download',
        trackOpen: 'seo_grade11_english_term1_book_open',
    },
    {
        id: 'en-term-2',
        language: 'en',
        term: 2,
        title: 'Official Programming Book — English · Term 2',
        url: grade11CurriculumConfig.englishTerm2BookUrl,
        fileName: 'grade-11-programming-ai-english-book-term-2-2026-2027.pdf',
        fileSize: grade11CurriculumConfig.englishTerm2BookSize,
        details: [
            'Second Year Secondary — Baccalaureate',
            `Academic Year ${grade11CurriculumConfig.academicYear}`,
            'Official ministry book',
            'Term 2',
            'Language schools',
            'PDF',
        ],
        sheetLabel: 'English — Term 2',
        downloadLabel: 'Download Term 2 PDF',
        openLabel: 'Open Book',
        trackDownload: 'seo_grade11_english_term2_book_download',
        trackOpen: 'seo_grade11_english_term2_book_open',
    },
];

export const downloadBookGroups = [
    {
        id: 'arabic',
        title: 'طلاب العربي',
        subtitle: 'كتاب الوزارة الرسمي — الترم الأول والترم الثاني',
        books: downloadBooks.filter((book) => book.language === 'ar'),
    },
    {
        id: 'english',
        title: 'طلاب اللغات',
        subtitle: 'Official ministry book — Term 1 and Term 2',
        books: downloadBooks.filter((book) => book.language === 'en'),
    },
];

export const curriculumFaqItems = [
    {
        q: 'ما هو منهج البرمجة تانية ثانوي بكالوريا؟',
        a: 'هو منهج يجمع بين تكنولوجيا المعلومات، وأمن الأنظمة، وتطبيقات الويب، وتصميم المواقع، وجمع وتحليل البيانات، والتعلم الآلي والذكاء الاصطناعي التوليدي.',
    },
    {
        q: 'هل يوجد كتاب برمجة رسمي للوزارة لتانية ثانوي؟',
        a: 'نعم. يمكن تحميل كتاب البرمجة والذكاء الاصطناعي الرسمي للوزارة من هذه الصفحة، بنسخة عربي ونسخة لغات، وكل نسخة مقسمة إلى الترم الأول والترم الثاني.',
    },
    {
        q: 'هل كتاب الوزارة متاح لطلاب العربي واللغات؟',
        a: 'نعم. الصفحة توفر الكتاب العربي لطلاب المدارس العربي، والكتاب الإنجليزي لطلاب اللغات، مع ملفين منفصلين لكل ترم.',
    },
    {
        q: 'هل تحميل كتاب البرمجة الرسمي مجاني؟',
        a: 'نعم، يمكن تحميل ملفات PDF الخاصة بكتاب الوزارة مجانًا من روابط التحميل في الصفحة.',
    },
    {
        q: 'كم فصلًا يوجد في منهج البرمجة والذكاء الاصطناعي؟',
        a: 'يتكوّن المنهج من 7 فصول مترابطة تغطي أهم جوانب الأنظمة الرقمية الحديثة، بداية من تكنولوجيا المعلومات وحتى Machine Learning وGenerative AI.',
    },
    {
        q: 'هل المنهج متاح لطلاب عربي ولغات؟',
        a: 'نعم. شرح Coding With Ghonaim متاح لطلاب المدارس العربي واللغات، مع توضيح المصطلحات الإنجليزية المستخدمة في المنهج.',
    },
    {
        q: 'هل منهج تانية ثانوي يحتاج أساسيات أولى ثانوي؟',
        a: 'وجود أساس جيد هيساعدك تبدأ بشكل أقوى، ولذلك نوفر كورسًا تمهيديًا مجانيًا لمراجعة أهم أساسيات أولى ثانوي قبل بداية المنهج.',
    },
    {
        q: 'هل الكورس التمهيدي مجاني؟',
        a: 'نعم، الكورس التمهيدي متاح مجانًا على منصة Coding With Ghonaim.',
    },
    {
        q: 'أبدأ بالكورس التمهيدي أم كورس الشهر الأول؟',
        a: 'ابدأ بالكورس التمهيدي لو محتاج تراجع أو مش واثق من أساسياتك. ابدأ كورس الشهر الأول لو جاهز تبدأ منهج تانية ثانوي مباشرة.',
    },
    {
        q: 'هل الكورس يشمل الجزء النظري والعملي؟',
        a: 'نعم. الكورس يغطي المفاهيم النظرية، والتطبيقات العملية، والتدريبات، والأسئلة، والواجبات والاختبارات.',
    },
    {
        q: 'هل البرمجة مادة حفظ؟',
        a: 'لا. يوجد عدد من المصطلحات التي تحتاج مراجعة، لكن الفهم والربط والتطبيق هم الأساس في دراسة المادة.',
    },
    {
        q: 'هل أحتاج إلى خبرة كبيرة في البرمجة؟',
        a: 'لا. الشرح يبدأ تدريجيًا، والكورس التمهيدي يساعد الطالب الذي يحتاج إلى تأسيس إضافي.',
    },
    {
        q: 'هل يوجد شرح لمنهج البرمجة تانية ثانوي عربي ولغات؟',
        a: 'نعم. المحتوى مصمم لخدمة طلاب العربي واللغات، مع شرح واضح للمفاهيم والمصطلحات باللغة المناسبة لكل طالب.',
    },
    {
        q: 'كيف أشترك في كورس الشهر الأول؟',
        a: 'يمكنك الانتقال إلى صفحة كورس الشهر الأول لمعرفة تفاصيل المحتوى وطرق التفعيل والدفع المتاحة.',
    },
];

export const curriculumBenefits = [
    'فهم طريقة عمل الأنظمة الرقمية الحديثة.',
    'اكتساب وعي أفضل بالأمان والخصوصية.',
    'فهم أساسيات المواقع وتطبيقات الويب.',
    'التفكير في التصميم من وجهة نظر المستخدم.',
    'جمع البيانات وتنظيمها وتجهيزها.',
    'تحليل البيانات وقراءة الرسوم البيانية.',
    'فهم مبادئ Machine Learning وGenerative AI.',
    'ربط المفاهيم النظرية بمواقف وتطبيقات حقيقية.',
    'اكتشاف المجالات التقنية التي يمكن التخصص فيها مستقبلًا.',
];

export const curriculumCareerFields = [
    { title: 'Web Development', icon: 'Code2', label: 'تطوير الويب' },
    { title: 'UI/UX Design', icon: 'Palette', label: 'تصميم واجهات' },
    { title: 'Cybersecurity', icon: 'ShieldCheck', label: 'الأمن السيبراني' },
    { title: 'Data Analysis', icon: 'BarChart3', label: 'تحليل البيانات' },
    { title: 'Data Science', icon: 'FlaskConical', label: 'علم البيانات' },
    { title: 'Machine Learning', icon: 'Bot', label: 'التعلم الآلي' },
    { title: 'Artificial Intelligence', icon: 'Sparkles', label: 'الذكاء الاصطناعي' },
];

export const curriculumPrepSteps = [
    'راجع أساسيات الكمبيوتر والإنترنت.',
    'راجع أهم ما درسته في Programming & AI في أولى ثانوي.',
    'قوّي أساسيات HTML وCSS وJavaScript.',
    'تدرّب على التفكير المنطقي وحل المشكلات.',
    'راجع الجداول والنسب والرسوم البيانية البسيطة.',
    'اهتم بأساسيات الأمان والخصوصية.',
    'استخدم أدوات AI كمساعد للتعلم، وليس للحصول على إجابات جاهزة.',
    'طبّق من خلال مشروعات وتدريبات صغيرة.',
];

export const curriculumSystemFlow = [
    { title: 'تكنولوجيا المعلومات', text: 'تحدد مكوّنات النظام.' },
    { title: 'الأمن وإدارة المخاطر', text: 'يحميان حسابات الطلاب وبياناتهم.' },
    { title: 'تطبيقات الويب', text: 'تشغّل المنصة وتربط المستخدم بالخادم وقاعدة البيانات.' },
    { title: 'تصميم المعلومات', text: 'يجعل المنصة واضحة وسهلة الاستخدام.' },
    { title: 'جمع البيانات', text: 'يسجل نشاط الطلاب ونتائجهم.' },
    { title: 'تحليل البيانات', text: 'يوضح مستوى كل طالب ونقاط ضعفه.' },
    { title: 'الذكاء الاصطناعي', text: 'يساعد في تقديم شرح أو تدريبات وتوصيات مناسبة.' },
];

export const heroAreaCards = [
    { label: 'IT', icon: 'Cpu' },
    { label: 'Security', icon: 'Shield' },
    { label: 'Web', icon: 'Globe' },
    { label: 'Design', icon: 'Layout' },
    { label: 'Data', icon: 'Database' },
    { label: 'Analysis', icon: 'BarChart3' },
    { label: 'AI', icon: 'Brain' },
];
